import React from 'react';
import PublicNavigation from '../../../shared/components/layout/PublicNavigation';
import Footer from '../../../shared/components/layout/Footer';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-grow py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-8 sm:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: January 14, 2026</p>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By creating an account, accessing, or using the Vester application or related services (the "Service"), you agree 
                to be bound by these Terms and Conditions ("Terms"). If you do not agree, you must not use the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We may update these Terms from time to time. Continued use of the Service after changes become effective 
                constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of the Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vester is a portfolio tracking and social visualization platform that allows users to manually enter 
                investment-related information, upload transaction data, view performance analytics, and interact socially 
                with other users.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">The Service:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Does not execute trades</li>
                <li>Does not connect to brokerages (unless explicitly enabled in the future)</li>
                <li>Does not verify the accuracy of user-entered data</li>
                <li>Is provided for informational and educational purposes only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. No Investment, Financial, Legal, or Tax Advice</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-800 font-medium">
                  Vester LLC is not a registered investment advisor, broker-dealer, or financial institution.
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nothing provided through the Service constitutes investment advice, financial advice, legal advice, tax advice, 
                or a recommendation to buy, sell, or hold any security or asset.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">You acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Any decisions you make based on information displayed in the Service are made solely at your own risk</li>
                <li>You should consult a licensed professional before making financial or investment decisions</li>
                <li>No fiduciary relationship is created between you and Vester LLC</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User-Generated Content & Data Accuracy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All portfolio data, transaction data, performance metrics, and social views within the Service are user-generated 
                or derived from user-provided inputs.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">You acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Data displayed in the Service may be inaccurate, incomplete, misleading, delayed, or fabricated</li>
                <li>Users may enter arbitrary or hypothetical values</li>
                <li>Friend portfolio views are not verified and may not reflect real investments</li>
                <li>Vester LLC does not validate or audit user-entered data</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You agree not to rely on any data within the Service for making investment or financial decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Past Performance Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                Any historical performance data displayed in the Service is for illustrative purposes only.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Past performance does not guarantee future results. Market conditions change, and displayed metrics may not 
                reflect real-world outcomes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Social Features & Communications</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service includes social features such as friend connections and messaging.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">You acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Communications are between users</li>
                <li>Vester LLC is not responsible for the accuracy, legality, or appropriateness of user communications</li>
                <li>We are not obligated to monitor user communications</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                However, we reserve the right to review, remove, or restrict content or accounts if we believe it violates 
                these Terms or applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Acceptable Use & Community Conduct</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to use the Service to engage in any activity that is unlawful, abusive, harassing, threatening, 
                defamatory, obscene, discriminatory, fraudulent, or otherwise objectionable, as determined by Vester LLC in 
                its sole discretion.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Prohibited conduct includes, but is not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Harassment or threats toward other users</li>
                <li>Hate speech or discriminatory behavior</li>
                <li>Fraud, impersonation, or misrepresentation</li>
                <li>Violations of applicable financial or securities laws</li>
                <li>Interference with the Service or other users</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We may suspend or terminate accounts, remove content, or restrict access with or without notice for violations 
                or suspected violations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Illegal Activity & Legal Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may not use the Service to engage in unlawful, fraudulent, or deceptive activity, including violations 
                of applicable securities or financial regulations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We are not obligated to monitor activity or communications. However, we may review or disclose user data if 
                required by law, legal process, or if we believe in good faith that such action is necessary to protect the 
                rights, safety, or security of Vester LLC, its users, or the public.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service may rely on third-party service providers to operate and host data. Any future integrations with 
                third-party platforms will be optional and subject to additional disclosures and permissions at the time they 
                are enabled.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Vester LLC is not responsible for third-party service outages, errors, or inaccuracies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Beta / Experimental Features</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service is currently provided as a beta product.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">You acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Features may change, break, or be removed</li>
                <li>Calculations may be incorrect</li>
                <li>Data may be lost</li>
                <li>The Service may be unavailable at times</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Use of the Service during beta is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service is provided "as is" and "as available", without warranties of any kind, express or implied, 
                including accuracy, reliability, availability, or fitness for a particular purpose.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, Vester LLC shall not be liable for any indirect, incidental, 
                consequential, or special damages, including loss of profits, data, or investment losses.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our total liability for any claim shall not exceed the greater of:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li>The amount paid by you to Vester LLC in the past 12 months, or</li>
                <li>$100 USD</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless Vester LLC from any claims, damages, liabilities, or expenses arising 
                from your use of the Service, your content, or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We may suspend or terminate your account at any time, with or without notice, for any reason, including 
                violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All intellectual property related to the Service belongs to Vester LLC. You are granted a limited, 
                non-transferable license to use the Service for personal, non-commercial purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Governing Law & Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms are governed by the laws of the State of Colorado, without regard to conflict of laws principles.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Any disputes shall be resolved through binding arbitration on an individual basis. Class actions are waived.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For questions regarding these Terms, contact:
              </p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>Vester LLC</strong><br />
                  Email: <a href="mailto:support@vesterapp.com" className="text-green-600 hover:text-green-500 underline">support@vesterapp.com</a>
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              By using Vester, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsPage;
